import React, { useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  Box, Chip
} from "@mui/material";
import {
  Visibility,
  ShoppingCart,
  AttachMoney,
  Inventory,
} from "@mui/icons-material";
import { Line } from "react-chartjs-2";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { getAllProduct } from "../../State/Product/Action";
import { getIncome, getOrders, getOrderStats, getRecentOrders } from "../../State/Order/Action";
import { getPayments } from "../../State/Payment/Action";
import dayjs from "dayjs";
import ImagePopup from "../../../Common/ImagePopup";
import defaultImage from "../../../Common/productDefaultImage.png"

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const { vendorProducts, vendorOrder, vendorPayments } = useSelector(store => store);
  const dispatch = useDispatch();
  const [imageOpen, setImageOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState("");

  useEffect(() => {
    dispatch(getAllProduct());
    dispatch(getOrders())
    dispatch(getRecentOrders())
    dispatch(getPayments());
    dispatch(getIncome());
    dispatch(getOrderStats());
  }, [dispatch]);

  const getStatusChip = (status) => {
    const colorMap = {
      "PENDING": "warning",
      "PLACED": "dark",
      "SHIPPED": "info",
      "DELIVERED": "success",
      "CANCELLED": "error"
    };
    return <Chip label={status} color={colorMap[status] || "default"} variant="contained" sx={{ fontSize: "0.7rem", height: "24px", borderRadius: "12px", fontWeight: "bold" }} />;
  };



  // Dynamic metric cards
  const metrics = [{ title: "Total Products", value: vendorProducts?.products?.response?.length || 0, change: "59.3%", icon: <Inventory />, description: "You made an extra 35,000 this year" }, { title: "Approved Products", value: vendorProducts?.products?.response?.filter(product => product.productStatus === "APPROVED")?.length || 0, change: "70.5%", icon: <Visibility />, description: "You made an extra 8,900 this year" }, { title: "Total Orders", value: vendorOrder?.orders?.response?.length || 0, change: "27.4%", icon: <ShoppingCart />, description: "You made an extra 1,943 this year" }, {
    title: "Total Sales",
    value: `₹${(
      vendorPayments?.payments?.response
        ?.filter(p => p?.adminPaymentStatus === "TRANSFER")
        ?.reduce((t, p) => t + (p?.order?.orderItems || []).reduce(
          (s, i) => s + (Number(i?.variant?.vendorPrice) || 0) * (Number(i?.quantity) || 0),
          0
        ), 0) || 0
    ).toFixed(2)}`, change: "27.4%", icon: <AttachMoney />, description: "You made an extra $20,395 this year"
  }];


  const rows =
    vendorOrder.recentOrders?.response?.filter((order) => order.orderStatus === "PENDING")?.map((order, index) => ({
      id: `${order.id}-${order?.orderItems?.map((item) => item?.product?.id).join("-")}-${index}`, // Unique ID for DataGrid
      orderId: order.orderId,
      orderDate: order.orderDate,
      productTitle: order?.orderItems?.map((item) => item?.product?.title).join(", ") || "----",
      productImages: order?.orderItems
        ?.flatMap((item) =>
          (item?.product?.images || [])
            .filter((img) => img.isPrimary)
            .map((img) => img.fullUrl)
        )
        ?.filter(Boolean)
        ?.join(",") || "----",
      productName: order?.orderItems?.map((item) => item?.product?.productCategory?.name).join(", ") || "----",
      quantity: order?.orderItems?.map((item) => item?.quantity).join(", ") || 0,
      vendorPrice: order?.orderItems?.map((item) => item?.variant?.vendorPrice?.toFixed(2)).join(", ") || 0,
      size: order?.orderItems?.map((item) => item?.size).join(", ") || "----",
      color: order?.orderItems?.map((item) => item?.color).join(", ") || "----",
      totalQuantity: order.totalItem || 0,
      totalPrice: order?.orderItems?.reduce(
        (sum, item) => sum + ((item?.variant?.vendorPrice || 0.00) * (item?.quantity || 0)),
        0
      ).toFixed(2),
      deliveryDate: order.deliveryDate,
      dispatchDate: order.dispatchDate,
      orderStatus: order.orderStatus
    })
    ) || [];

  // Define columns
  const columns = [
    {
      field: "serialNo",
      headerName: "S.No.",
      width: 60,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1
    },
    { field: "orderId", headerName: "Order ID", width: 120 },
    { field: "orderDate", headerName: "Order Date", width: 120, renderCell: (params) => params.value ? dayjs(params?.value).format("DD MMM YYYY HH:mm") : "----", },
    {
      field: "orderStatus",
      headerName: "Order Status",
      width: 100,
      renderCell: (params) => getStatusChip(params.value)

    },
    { field: "productTitle", headerName: "Product Title", width: 120 },
    {
      field: "productImages",
      headerName: "Image(s)",
      width: 100,
      renderCell: (params) => {
        const images = params?.value
          ? params.value.split(",").map((url) => url.trim()) // multiple comma separated
          : [];

        if (images.length === 0) {
          return <span>----</span>;
        }

        return (
          <div style={{ display: "flex", gap: "5px" }}>
            {images.map((url, index) => (
              <img
                key={index}
                onClick={() => {
                  setImageOpen(true);
                  setImageUrl(url || defaultImage);
                }}
                src={url || defaultImage}
                alt={`aadhaar_image_${index}`}
                width={30}
                height={30}
                style={{ borderRadius: 8, objectFit: "cover", cursor: "pointer" }}
              />
            ))}
          </div>
        );
      },
    },
    { field: "productName", headerName: "Product Name", width: 120 },
    { field: "quantity", headerName: "Quantity", width: 100 },
    { field: "vendorPrice", headerName: "Price", width: 100 },
    { field: "color", headerName: "Color", width: 80 },
    { field: "size", headerName: "Size", width: 80 },
    { field: "totalQuantity", headerName: "Total Quantity", width: 90 },
    { field: "totalPrice", headerName: "Total Price", width: 80 },
    { field: "deliveryDate", headerName: "Delivery Date", width: 100, renderCell: (params) => params.value ? dayjs(params?.value).format("DD MMM YYYY") : "----", },
    { field: "dispatchDate", headerName: "Dispatch Date", width: 100, renderCell: (params) => params.value ? dayjs(params?.value).format("DD MMM YYYY") : "----", },
  ];

  const analyticsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          font: { size: 12 },
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  const analyticsData = {
    labels: ["Total Products",
      "Approved",
      "Rejected",
      "Pending",],
    datasets: [
      {
        label: "Products",
        data: [
          vendorProducts?.products?.response?.length || 0,
          vendorProducts?.products?.response?.filter(p => p.productStatus === "APPROVED")?.length || 0,
          vendorProducts?.products?.response?.filter(p => p.productStatus === "REJECTED")?.length || 0,
          vendorProducts?.products?.response?.filter(p => p.productStatus === "PENDING")?.length || 0,
        ],
        backgroundColor: "#449AEF",
        borderColor: "#449AEF",
        borderWidth: 2,
        fill: false,
        tension: 0.3,
        pointBackgroundColor: "#449AEF",
        pointBorderColor: "#449AEF",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#449AEF"
      },
    ],
  };

  const analyticsOrderData = {
    labels: ["Total Orders",
      "Pending ",
      "Placed ",
      "Shipped ",
      "Delivered ",
      "Cancelled ",],
    datasets: [
      {
        label: "Orders Status",
        data: [
          vendorOrder?.orders?.response?.length || 0,
          vendorOrder?.orders?.response?.filter(o => o.orderStatus === "PENDING")?.length || 0,
          vendorOrder?.orders?.response?.filter(o => o.orderStatus === "PLACED")?.length || 0,
          vendorOrder?.orders?.response?.filter(o => o.orderStatus === "SHIPPED")?.length || 0,
          vendorOrder?.orders?.response?.filter(o => o.orderStatus === "DELIVERED")?.length || 0,
          vendorOrder?.orders?.response?.filter(o => o.orderStatus === "CANCELLED")?.length || 0,
        ],
        backgroundColor: "#449AEF",
        borderColor: "#449AEF",
        borderWidth: 2,
        fill: false,
        tension: 0.3,
        pointBackgroundColor: "#449AEF",
        pointBorderColor: "#449AEF",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#449AEF"
      },
    ],
  };

  const analyticsEmployeeOrderData = {
    labels: ["Orders Delivered", "On Time", "Before Time", "Beyond Time", "Issues/Cancelled"],
    datasets: [
      {
        label: "Orders Delivered",
        data: [
          vendorOrder?.orderStats?.accepted || 0,
          vendorOrder?.orderStats?.deliveredOnTime || 0,
          vendorOrder?.orderStats?.deliveredBeforeTime || 0,
          vendorOrder?.orderStats?.deliveredBeyondTime || 0,
          vendorOrder?.orderStats?.issuesOrCancelled || 0,
        ],
        backgroundColor: "#449AEF",
        borderColor: "#449AEF",
        borderWidth: 2,
        fill: false,
        tension: 0.3,
        pointBackgroundColor: "#449AEF",
        pointBorderColor: "#449AEF",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#449AEF"
      },
    ],
  };
  const analyticsEmployeeAcceptOrderData = {
    labels: ["Total Orders", "Employee Accept", "Pending"],
    datasets: [
      {
        label: "Orders",
        data: [
          vendorOrder?.orders?.response?.length || 0,
          vendorOrder?.orders?.response?.filter(o => o.employeeAcceptDate !== null && o.employee !== null)?.length || 0,
          vendorOrder?.orders?.response?.filter(o => o.employeeAcceptDate === null && o.employee === null && o.orderStatus === "PENDING")?.length || 0,
        ],
        backgroundColor: "#449AEF",
        borderColor: "#449AEF",
        borderWidth: 2,
        fill: false,
        tension: 0.3,
        pointBackgroundColor: "#449AEF",
        pointBorderColor: "#449AEF",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#449AEF"
      },
    ],
  };

  return (
    <Box sx={{ flexGrow: 1, p: 2, minHeight: "100vh", bgcolor: "#f9f9f9" }}>
      <Grid container spacing={1}>
        {/* Metrics Cards */}
        {metrics.map((metric, index) =>
          <Grid item xs={12} sm={6} md={6} lg={3} key={index}>
            <Card
              sx={{
                height: "100%",
                boxShadow: 3,
                borderRadius: 3,
                transition: "0.3s",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: 6
                }
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  {metric.title}
                </Typography>

                <Box display="flex" alignItems="center" mt={0.5}>
                  {/* Icon */}
                  <Box
                    sx={{
                      bgcolor: "#449AEF",
                      color: "white",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    {metric.icon}
                  </Box>

                  {/* Value */}
                  {/* <Typography variant="h6" marginLeft={0.5}>
                    {metric.value}
                  </Typography> */}
                  <Typography
                    variant="h6"
                    marginLeft={0.5}
                    sx={{
                      fontSize: "clamp(0.5rem, 1.5vw, 1rem)", // auto adjust
                      maxWidth: "170px", // fix width
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {metric.value}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12} md={8}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Products</Typography>
          </Box>

          <Card sx={{ height: 350 }}>
            <CardContent sx={{ height: "100%" }}>
              <Box sx={{ height: "100%" }}>
                <Line data={analyticsData} options={analyticsOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>


        {/* Income Overview */}
        <Grid item xs={12} md={4}>
          <Typography variant="h6" mb={2}>
            Income
          </Typography>
          <Card
            sx={{
              height: { xs: "auto", md: 350 },
              p: { xs: 1.5, md: 2 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <CardContent>
              {[
                { label: "Today", value: vendorOrder?.income?.today },
                { label: "This Week", value: vendorOrder?.income?.week },
                { label: "This Month", value: vendorOrder?.income?.month },
                { label: "This Year", value: vendorOrder?.income?.year },
              ].map(({ label, value }, index) => (
                <Box key={index} sx={{ mb: index !== 3 ? 2 : 0 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {label} Statistics
                  </Typography>
                  <Typography variant="h6" sx={{ wordWrap: "break-word" }}>
                    ₹{value?.toFixed(2) || 0.00}
                  </Typography>
                  {index !== 3 && <Divider sx={{ my: 1 }} />}
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>


        <Grid item xs={12} md={12}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Orders</Typography>
          </Box>

          <Card sx={{ height: 350 }}>
            <CardContent sx={{ height: "100%" }}>
              <Box sx={{ height: "100%" }}>
                <Line data={analyticsOrderData} options={analyticsOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Accept Orders</Typography>
          </Box>

          <Card sx={{ height: 350 }}>
            <CardContent sx={{ height: "100%" }}>
              <Box sx={{ height: "100%" }}>
                <Line data={analyticsEmployeeAcceptOrderData} options={analyticsOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Orders Delivered Statistics</Typography>
          </Box>

          <Card sx={{ height: 350 }}>
            <CardContent sx={{ height: "100%" }}>
              <Box sx={{ height: "100%" }}>
                <Line data={analyticsEmployeeOrderData} options={analyticsOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Orders */}
        <Grid item xs={12} md={8}>
          <Typography variant="h6" mb={2}>
            Recent Orders
          </Typography>
          <Card>
            <CardContent>
              <Box sx={{ height: "100%", width: "100%" }}>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  pagination
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10, page: 0 } },
                  }}
                  pageSizeOptions={[10, 25, 50]}
                  getRowId={(row) => row.id}
                  disableSelectionOnClick
                  density="compact"
                  experimentalFeatures={{ virtualization: true }}
                  loading={vendorOrder.loading}
                  autoHeight={true}
                  sx={{
                    borderRadius: 2,
                    fontSize: "0.8rem",
                    "& .MuiDataGrid-cell": {
                      fontSize: "0.8rem",
                      border: "1px solid rgb(204, 201, 201)",
                      minHeight: "36px !important",
                      height: "36px !important",
                      lineHeight: "1.2",
                      paddingTop: "4px",
                      paddingBottom: "4px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                    "& .MuiDataGrid-columnHeader": {
                      backgroundColor: "#449AEF",
                      color: "#fff",
                      fontWeight: "bold",
                      fontSize: "0.8rem",
                      padding: "8px",
                      border: "1px solid rgb(204, 201, 201)",
                      minHeight: "36px !important",
                      height: "36px !important",
                      lineHeight: "1.2",
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontSize: "0.8rem",
                      fontWeight: 600,
                    },
                    "& .MuiDataGrid-row": {
                      maxHeight: "36px !important",
                      minHeight: "36px !important",
                    },
                    "& .MuiDataGrid-row:nth-of-type(odd)": {
                      backgroundColor: "#f5f5f5",
                    },
                    "& .MuiDataGrid-row:nth-of-type(even)": {
                      backgroundColor: "#ffffff",
                    },
                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Transaction History */}
        <Grid item xs={12} md={4}>
          <Typography variant="h6" mb={2}>
            Recent Delivered Orders
          </Typography>
          <Card sx={{ height: 400, p: { xs: 1.5, md: 2 } }}>
            <CardContent>
              <List disablePadding>
                {vendorPayments?.payments?.response?.filter(payment => payment?.adminPaymentStatus === "TRANSFER")?.length > 0 ? (
                  vendorPayments?.payments?.response?.filter(payment => payment?.adminPaymentStatus === "TRANSFER")
                    ?.slice(0, 4)
                    ?.map((payment, index) => (
                      <React.Fragment key={payment?.id || index}>
                        <ListItem
                          alignItems="flex-start"
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: { xs: "flex-start", sm: "center" },
                            gap: 1,
                          }}
                        >
                          <ListItemText
                            primary={`Order #${payment?.paymentId?.slice(0, 5)}...`}
                            secondary={
                              payment?.createdAt
                                ? dayjs(payment.createdAt).format("DD MMM YYYY HH:mm")
                                : "----"
                            }
                          />
                          <Typography
                            variant="body2"
                            sx={{ color: "success.main", minWidth: "70px", textAlign: "right" }}
                          >
                            {(() => {
                              const orderItems = payment?.order?.orderItems || [];

                              const orderTotal = orderItems.reduce((sum, item) => {
                                const price = item?.variant?.vendorPrice || 0;
                                const quantity = item?.quantity || 0;
                                return sum + price * quantity;
                              }, 0);

                              return `₹${orderTotal?.toFixed(2)}`;
                            })()}

                          </Typography>
                        </ListItem>
                        {index < 3 && <Divider sx={{ my: 1 }} />}
                      </React.Fragment>
                    ))) : (
                  <ListItem>
                    <ListItemText primary="No transactions available" />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Common Popup */}
      <ImagePopup
        open={imageOpen}
        onClose={() => setImageOpen(false)}
        imageUrl={imageUrl}
      />
    </Box>
  );
};

export default Dashboard;
